import { api, type HistoricalFigure } from '../api'
import { buildThreadCreatePayload } from './threadPayload.js'

const FIGURE_SLUG_REGISTRY_LIMIT = 500
export const OUT_OF_DATE_FIGURE_MESSAGE = 'Selected figure is out of date—please reselect.'

let slugRegistry: Set<string> | null = null
let inflight: Promise<Set<string>> | null = null

const normalizeSlug = (value?: string | null): string | null => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return trimmed.length ? trimmed : null
}

const buildRegistry = (figures: Array<Pick<HistoricalFigure, 'slug'>>): Set<string> => {
  const next = new Set<string>()
  figures.forEach((figure) => {
    const normalized = normalizeSlug(figure?.slug)
    if (normalized) {
      next.add(normalized)
    }
  })
  return next
}

export const loadFigureSlugRegistry = async (): Promise<Set<string>> => {
  if (slugRegistry) return slugRegistry
  if (inflight) return inflight

  inflight = api
    .listFigures({ limit: FIGURE_SLUG_REGISTRY_LIMIT })
    .then((figures) => {
      const registry = buildRegistry(Array.isArray(figures) ? figures : [])
      slugRegistry = registry
      inflight = null
      return registry
    })
    .catch((error) => {
      inflight = null
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.warn('[FIGURE_SLUG_REGISTRY] Failed to load canonical figure slugs', error)
      }
      throw error
    })

  return inflight
}

export const primeFigureSlugRegistry = (figures?: Array<Pick<HistoricalFigure, 'slug'> | null> | null): void => {
  if (!figures || figures.length === 0) return
  const registry = slugRegistry ?? new Set<string>()
  let mutated = false

  figures.forEach((figure) => {
    if (!figure) return
    const normalized = normalizeSlug(figure.slug)
    if (normalized && !registry.has(normalized)) {
      registry.add(normalized)
      mutated = true
    }
  })

  if (mutated) {
    slugRegistry = registry
  }
}

export const isFigureSlugKnown = (slug?: string | null): boolean => {
  const normalized = normalizeSlug(slug)
  if (!normalized) return false
  return slugRegistry?.has(normalized) ?? false
}

export const getFigureSlugRegistrySize = (): number => slugRegistry?.size ?? 0

export const ensureValidFigureSlug = async (slug?: string | null): Promise<boolean> => {
  const normalized = normalizeSlug(slug)
  if (!normalized) return false

  const registry = slugRegistry ?? (await loadFigureSlugRegistry())
  const isValid = registry.has(normalized)

  if (!isValid) {
    // eslint-disable-next-line no-console
    console.warn('[FIGURE_SLUG_REGISTRY] Rejecting unknown figure slug', {
      slug: normalized,
      registrySize: registry.size,
    })
  }

  return isValid
}

export { buildThreadCreatePayload }
